import {
  differenceInCalendarDays,
  format,
  isToday,
  isYesterday,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns'
import type { EstatisticaEstudo, MatriculaListItem } from '@/types'

type EstatisticaDeProgresso = Pick<
  EstatisticaEstudo,
  'percentualProgressoMaisAvancado' | 'updatedAt'
>

export function calculateProgress(
  estatisticas: EstatisticaDeProgresso[],
  totalModulos: number,
): number {
  if (!totalModulos || !estatisticas.length) return 0
  const total = estatisticas.reduce(
    (soma, estatistica) => soma + (estatistica.percentualProgressoMaisAvancado ?? 0),
    0,
  )
  return Math.round(total / totalModulos)
}

export function faseDoCurso(estatisticas: EstatisticaDeProgresso[], progresso: number): string {
  // Sem estatística, o aluno nunca abriu o curso; progresso zero com estatística significa que abriu e parou ali.
  if (!estatisticas.length) return 'Não iniciado'
  if (progresso === 100) return 'Concluído'
  if (progresso === 0) return 'Inativo'
  return 'Em andamento'
}

export function concluiuOsModulos(
  estatisticas: EstatisticaDeProgresso[],
  totalModulos: number,
): boolean {
  if (!totalModulos) return false
  const concluidos = estatisticas.filter(
    (estatistica) => (estatistica.percentualProgressoMaisAvancado ?? 0) >= 100,
  )
  return concluidos.length >= totalModulos
}

/**
 * Fora de SP e RS o DETRAN conta um dia a menos, como em validateEmissaoCertificado no backend.
 * Zero significa que a espera acabou; null, que não há prazo ou o curso nem começou no DETRAN.
 */
export function diasAteConclusaoNoDetran(
  matricula: Pick<MatriculaListItem, 'dataInicioCurso' | 'uf' | 'ufMatriculaDetran' | 'curso'>,
): number | null {
  const diasDoCurso = matricula.curso.diasParaEmissaoCertificado
  if (!diasDoCurso || !matricula.dataInicioCurso) return null

  const ufDaMatricula = matricula.ufMatriculaDetran ?? matricula.uf
  const diasExigidos = ['SP', 'RS'].includes(ufDaMatricula) ? diasDoCurso : diasDoCurso - 1
  const restantes = differenceInCalendarDays(
    startOfDay(parseISO(matricula.dataInicioCurso)),
    startOfDay(subDays(new Date(), diasExigidos)),
  )
  return Math.max(restantes, 0)
}

export function ultimaAtividade(estatisticas: EstatisticaDeProgresso[]): string | null {
  const datas = estatisticas.map((estatistica) => estatistica.updatedAt).filter(Boolean)
  if (!datas.length) return null
  return datas.reduce((maisRecente, data) => (data > maisRecente ? data : maisRecente))
}

export function formatUltimaAtividade(data: string | null): string {
  if (!data) return 'Nunca acessou'
  try {
    const quando = parseISO(data)
    if (isToday(quando)) return `Hoje · ${format(quando, 'HH:mm')}`
    if (isYesterday(quando)) return `Ontem · ${format(quando, 'HH:mm')}`
    const dias = differenceInCalendarDays(new Date(), quando)
    if (dias <= 30) return `Há ${dias} dias`
    return format(quando, 'dd/MM/yyyy')
  } catch {
    return data
  }
}
